import React from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import {
  Home,
  About,
  Contact,
  Menu,
  Vacancies,
  NoVacancies,
  GalleryPage,
  Signin,
  Signup,
  Reservation,
} from './pages';
import { TbBrandBooking } from 'react-icons/tb';
import { StyleSheet, css } from 'aphrodite';

import './App.css';

const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// --- ROUTE TRANSITION WRAPPER ---
const AnimatedPage = ({ children }) => {
  return (
    <div className={css(fadeStyles.fadeBackground)}>
      <div className={css(fadeStyles.routeCurtain)} aria-hidden="true" />
      <div className={css(fadeStyles.routeAccent)} aria-hidden="true" />
      <div className={css(fadeStyles.fadeWrapper)}>{children}</div>
    </div>
  );
};

function App() {
  const location = useLocation();

  return (
    <div id="app" className={css(styles.app)}>
      <ScrollToTop />

      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <AnimatedPage>
              <Home />
            </AnimatedPage>
          }
        />
        <Route
          path="/about"
          element={
            <AnimatedPage>
              <About />
            </AnimatedPage>
          }
        />
        <Route
          path="/contact"
          element={
            <AnimatedPage>
              <Contact />
            </AnimatedPage>
          }
        />
        <Route
          path="/menu"
          element={
            <AnimatedPage>
              <Menu />
            </AnimatedPage>
          }
        />
        <Route
          path="/vacancies"
          element={
            <AnimatedPage>
              <Vacancies />
            </AnimatedPage>
          }
        />
        <Route
          path="/no-vacancies"
          element={
            <AnimatedPage>
              <NoVacancies />
            </AnimatedPage>
          }
        />
        <Route
          path="/gallery"
          element={
            <AnimatedPage>
              <GalleryPage />
            </AnimatedPage>
          }
        />
        <Route
          path="/signin"
          element={
            <AnimatedPage>
              <Signin />
            </AnimatedPage>
          }
        />
        <Route
          path="/signup"
          element={
            <AnimatedPage>
              <Signup />
            </AnimatedPage>
          }
        />
        <Route
          path="/reservation"
          element={
            <AnimatedPage>
              <Reservation />
            </AnimatedPage>
          }
        />
      </Routes>

      <div className={css(styles.bookingWrapper)}>
        <div className={css(styles.tooltip)}>
          Book Now
          <div className="arrow" />
        </div>
        <Link to="/reservation" className={css(styles.bookingButton)}>
          <TbBrandBooking className={css(styles.icon)} />
        </Link>
      </div>
    </div>
  );
}

// --- Styles ---
const styles = StyleSheet.create({
  app: {
    position: 'relative',
    minHeight: '100vh',
  },

  bookingWrapper: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    zIndex: 999,
  },

  tooltip: {
    backgroundColor: 'var(--color-paper)',
    color: 'var(--color-green)',
    fontFamily: 'var(--font-ui)',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '.1em',
    textTransform: 'uppercase',
    padding: '6px 10px',
    border: '1px solid rgba(35,79,64,.14)',
    borderRadius: '999px',
    marginBottom: '6px',
    boxShadow: '0px 2px 6px rgba(0,0,0,0.3)',
    whiteSpace: 'nowrap',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  bookingButton: {
    background: 'var(--color-green)',
    width: '58px',
    height: '58px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textDecoration: 'none',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',

    ':hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 14px 35px rgba(23,60,50,.28)',
    },
  },

  icon: {
    color: 'var(--color-white)',
    fontSize: '28px',
  },
});

// --- PAGE FADE STYLES ---
const fadeStyles = StyleSheet.create({
  fadeBackground: {
    backgroundColor: 'var(--color-cream)',
    minHeight: '100vh',
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
  },

  fadeWrapper: {
    animationName: {
      '0%': { opacity: 0, transform: 'translateY(18px)', filter: 'blur(4px)' },
      '45%': { opacity: 0.35, filter: 'blur(2px)' },
      '100%': { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' },
    },
    animationDuration: '.72s',
    animationTimingFunction: 'cubic-bezier(.22,1,.36,1)',
    // Apply the entrance frame while animating, then release transform/filter.
    // Keeping filter: blur(0) after the animation creates a containing block and
    // makes fixed overlays (such as the menu viewer) sit lower in the document.
    animationFillMode: 'backwards',
  },

  routeCurtain: {
    position: 'fixed',
    inset: 0,
    zIndex: 5000,
    pointerEvents: 'none',
    background: 'var(--color-green)',
    transformOrigin: 'left center',
    animationName: {
      '0%': { transform: 'scaleX(0)' },
      '42%': { transform: 'scaleX(1)' },
      '58%': { transform: 'scaleX(1)', transformOrigin: 'right center' },
      '100%': { transform: 'scaleX(0)', transformOrigin: 'right center' },
    },
    animationDuration: '.72s',
    animationTimingFunction: 'cubic-bezier(.76,0,.24,1)',
    animationFillMode: 'both',
  },

  routeAccent: {
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    width: '5px',
    zIndex: 5001,
    pointerEvents: 'none',
    background: 'var(--color-golden)',
    animationName: {
      '0%': { transform: 'translateX(-8px)', opacity: 0 },
      '38%': { transform: 'translateX(100vw)', opacity: 1 },
      '100%': { transform: 'translateX(100vw)', opacity: 0 },
    },
    animationDuration: '.72s',
    animationTimingFunction: 'cubic-bezier(.76,0,.24,1)',
    animationFillMode: 'both',
  },
});

export default App;
